/**
 * A Motion step that plays when the thing on screen is EXCHANGED, and never when it first appears.
 *
 * ── The defect this exists to remove ─────────────────────────────────────────────────────────
 * An entrance keyed on data replays when the data lands. A pane keyed `plan.id ?? 'none'` holds
 * `'none'` while the list is on its way and takes the first plan's id the moment it arrives, so
 * React remounts it and the screen plays its arrival animation ON TOP of the hand-over the
 * skeleton was already performing. The trainer sees the same content enter twice, which is what
 * the owner reported on 21 September 2026.
 *
 * The rule, and it is the whole module: **an entrance plays once, on a box that stands from the
 * first frame; a key that must replay on the trainer's own pointer is silent for the first key it
 * shows.** Swapping plan for plan under a moving pointer is a gesture the trainer caused and it
 * still plays. The list arriving is not.
 *
 * ── `null` is "nothing to show yet", and crossing it is not a change ─────────────────────────
 * This is the field `useSwapReplay` beside it does not have, and the reason that hook could not
 * simply be reused. Its key is `string | number`, so a surface with an empty state has to spell
 * that state as a key — and then entering it and leaving it are both changes, which is exactly the
 * replay this module refuses. Here `null` means the surface is waiting or empty: going from `null`
 * to a key is an arrival, going from a key to `null` is a departure, and neither is an exchange.
 * A departure also clears the count: the surface is showing nothing again, so whatever it shows
 * next is a first showing and plays nothing, however many exchanges happened before the wait.
 *
 * ── Why the comparison runs during RENDER ────────────────────────────────────────────────────
 * React's own "adjusting state when a prop changes" pattern, the same one `useSwapReplay` uses.
 * The re-render happens before the browser paints, so no frame is drawn with the stale value. An
 * effect would paint the new content first and animate it afterwards — a jump followed by an
 * animation, which is the defect. A ref would be a write during render, which the React Compiler
 * refuses outright.
 *
 * Reduced motion needs no handling here: it is already a parameter of `screenMotion`, which
 * returns a step whose start state IS its end state, and this module only decides WHETHER to play
 * the step it was given.
 */

import { useState } from 'react';

import type { ScreenMotionStep } from './screenMotion';

/** A Motion step with its `initial` suppressed until the key has actually changed once. */
export interface MotionOnKeyChange {
  /** `false` tells Motion to start at `animate` — no entrance, no frame at the initial state. */
  readonly initial: ScreenMotionStep['initial'] | false;
  readonly animate: ScreenMotionStep['animate'];
  readonly transition: ScreenMotionStep['transition'];
}

/**
 * @param key What is being shown, or `null` when nothing is. Entering or leaving `null` is not a
 *   change; an exchange of one key for another is.
 * @param step The step to play on an exchange, from `screenMotion`.
 */
export function useMotionOnKeyChange(key: string | null, step: ScreenMotionStep): MotionOnKeyChange {
  const [shown, setShown] = useState<{ readonly key: string | null; readonly changes: number }>({
    key,
    changes: 0,
  });

  if (shown.key !== key) {
    // Only an exchange counts. `null` on either side is an arrival or a departure, and the element
    // is mounting or unmounting for that — there is nothing to cross-fade between.
    const exchanged = shown.key !== null && key !== null;
    // And a departure FORGETS the exchanges before it. The surface went back to showing nothing, so
    // the next key is a first showing again and has to be as silent as the first one was: without
    // this, an inspector that had swapped once replayed its entrance on whatever the list brought
    // back after a re-read.
    setShown({ key, changes: key === null ? 0 : shown.changes + (exchanged ? 1 : 0) });
  }

  return {
    initial: shown.changes > 0 ? step.initial : false,
    animate: step.animate,
    transition: step.transition,
  };
}
